import React from 'react'
import { useTranslation } from 'react-i18next'
import PageTitle from './PageTitle'
import { stores, pickStore } from '../data/amazonStores'
import './brand/brand.css'
import './HomePreview.css'
import BrandHeader from './brand/BrandHeader'
import BrandFooter from './brand/BrandFooter'
import { useBrandHead } from './brand/useBrandHead'
import homeBack from '../assets/home_back.jpg'
import products from '../assets/products.jpg'
import videoStill from '../assets/candle-pens/candle-pens-video-screenshot.jpg'
import fingerPaint from '../assets/home-preview/finger-paint.jpg'
import paintPens from '../assets/home-preview/paint-pens.jpg'
import ceramicPaint from '../assets/home-preview/ceramic-paint.jpg'
import fabricPaint from '../assets/home-preview/fabric-paint.jpg'
import geckoPencils from '../assets/home-preview/gecko-pencils.jpg'
import candlePens from '../assets/home-preview/candle-pens.jpg'

// Internal mockup of the proposed home page redesign (English only, noindex).
// Design source: Claude Design canvas "Colozoo — улучшения дизайна".

interface Product {
  name: string
  text: string
  image: string
  asin?: string
  to?: string
  isNew?: boolean
}

const productList: Product[] = [
  { name: 'Finger Paint', text: '8 colours with palette, rollers and brushes. Washes out of clothes at 30 °C.', image: fingerPaint, asin: 'B0C2552MK6' },
  { name: 'Paint Pens', text: '10 colours incl. gold and silver — stone, metal, glass, wood.', image: paintPens, asin: 'B07FW8LY6N' },
  { name: 'Glass & Porcelain Paint', text: '14 colours with brushes. Permanent after baking in the oven.', image: ceramicPaint, asin: 'B0D2B8KMMW' },
  { name: 'Fabric Paint', text: '14 colours with brushes. Permanent and machine-washable after ironing.', image: fabricPaint, asin: 'B0D92V8VKG' },
  { name: '3-in-1 Gecko Pencils', text: '48 colours with sharpener, brush and colouring book.', image: geckoPencils, asin: 'B0CS3P4SXR' },
  { name: 'Candle Pens', text: '16 colours. Decorate your own candles — tutorial inside.', image: candlePens, to: '/candle-pens', isNew: true },
]

const ArrowIcon = () => (
  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="m13 6 6 6-6 6" /></svg>
)

const HomePreview = () => {
  const { i18n } = useTranslation()
  const store = pickStore(i18n.language)
  const storeHost = store.domain
  const orderedStores = [ store, ...stores.filter((s) => s !== store) ]

  useBrandHead({ noindex: true })

  const productHref = (p: Product) => (p.to ? p.to : `https://www.${storeHost}/dp/${p.asin}`)

  return (
    <>
      <PageTitle title="Colozoo — Home redesign preview" />
      <div className="czb-page hp">
        <BrandHeader
          logoHref="#top"
          nav={[
            { href: '#products', label: 'Products' },
            { href: '/candle-pens', label: 'Candle Pens' },
            { href: '#courses', label: 'Online courses' },
            { href: '#story', label: 'Our story' },
          ]}
          cta={{ href: store.url, label: 'Shop on Amazon', external: true }}
        />

        <section id="top" className="hp-hero czb-jungle">
          <img src={homeBack} alt="" aria-hidden="true" className="czb-jungle-art" />
          <div className="hp-wrap hp-hero-row">
            <div className="hp-hero-text">
              <p className="hp-eyebrow hp-eyebrow-light">Art supplies designed by artists</p>
              <h1>Creating happiness!</h1>
              <p className="hp-lead">Bold, playful paints, pens and pencils for artists of all ages — made by two art students who wanted more from their materials.</p>
              <div className="hp-actions">
                <a href={store.url} target="_blank" rel="noopener noreferrer" className="czb-btn czb-btn-red">Shop on {store.domain} <ArrowIcon /></a>
                <a href="#courses" className="czb-btn czb-btn-white">Free online courses</a>
              </div>
              <p className="hp-hero-note">Shopping from another country? <a href="#stores">Choose your Amazon store</a></p>
            </div>
            <div className="hp-hero-media">
              <img src={products} alt="The Colozoo range: finger paint, paint pens, ceramic paint, fabric paint, gel pens and 3-in-1 Gecko pencils" />
            </div>
          </div>
        </section>

        <section className="hp-strip">
          <div className="hp-wrap hp-strip-row">
            <span>Designed by artists &amp; designers in Germany</span>
            <span>Safe and fun for kids &amp; families</span>
            <span>Available in {stores.length} Amazon stores</span>
          </div>
        </section>

        <section id="products" className="hp-section">
          <div className="hp-wrap">
            <div className="hp-section-head">
              <div>
                <h2>Find your next favourite</h2>
                <p className="hp-muted">Every set is tested on real projects — paper, ceramics, fabric, stone and candles.</p>
              </div>
              <a href={store.url} target="_blank" rel="noopener noreferrer" className="hp-link">See the full range on Amazon</a>
            </div>
            <div className="hp-products">
              {productList.map((p) => (
                <a
                  key={p.name}
                  href={productHref(p)}
                  {...(p.to ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
                  className={`hp-product${p.isNew ? ' hp-product-new' : ''}`}
                >
                  <div className="hp-product-img">
                    <img src={p.image} alt={`Colozoo ${p.name}`} loading="lazy" />
                    {p.isNew && <span className="hp-badge">New</span>}
                  </div>
                  <div className="hp-product-body">
                    <span className="hp-product-name">{p.name}</span>
                    <span className="hp-product-text">{p.text}</span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section id="story" className="hp-section hp-mist">
          <div className="hp-wrap hp-story">
            <div className="hp-story-head">
              <p className="hp-eyebrow">Our story</p>
              <h2>Made by artists. Loved by families.</h2>
            </div>
            <div className="hp-story-cols">
              <div>
                <h3>How we got our start</h3>
                <p>
                  Two art and design students in Germany were tired of dull, uninspiring supplies.
                  They wanted something fun, bold and full of energy — later, as parents, they saw
                  how creative tools bring families together.
                </p>
              </div>
              <div>
                <h3>What makes us different</h3>
                <p>
                  Every product is designed by artists who know what creative minds need — and by
                  parents who know what families care about. Thoughtful, safe and made for joyful exploration.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="courses" className="hp-section">
          <div className="hp-wrap">
            <div className="hp-courses">
              <div className="hp-courses-media">
                <img src={videoStill} alt="Still from a Colozoo video tutorial" />
                <span className="hp-play" aria-hidden="true"><svg width="28" height="28" viewBox="0 0 24 24" fill="#231F20"><path d="M8 5v14l11-7z" /></svg></span>
              </div>
              <div className="hp-courses-text">
                <p className="hp-eyebrow hp-eyebrow-mint">Included with your set</p>
                <h2>Learn with free video courses</h2>
                <p>
                  Watercolours, ink and crayons — step-by-step lessons with subtitles.
                  Unlock them with your Amazon order number.
                </p>
                <div className="hp-chips">
                  <span>Watercolours</span><span>Ink</span><span>Crayons</span>
                </div>
                <a href="/watercolours-course" className="czb-btn czb-btn-white">Unlock a course</a>
              </div>
            </div>
          </div>
        </section>

        <section id="stores" className="hp-section hp-sand">
          <div className="hp-wrap">
            <h2 className="hp-h2-sm">Shop in your country</h2>
            <p className="hp-muted">We picked your store from your language. Change it any time.</p>
            <div className="hp-stores">
              {orderedStores.map((s) => (
                <a
                  key={s.code}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`hp-store${s === store ? ' hp-store-active' : ''}`}
                >
                  <span className="hp-store-code">{s.code}</span>
                  {s.domain}{s === store ? ' · your store' : ''}
                </a>
              ))}
            </div>
          </div>
        </section>

        <BrandFooter />
      </div>
    </>
  )
}

export default HomePreview
